/**
 * The routes every page hangs off: the root, the public login page, and `appRoute`, the
 * authenticated layout (the shell). Features attach their routes to `appRoute` from their own
 * `routes.ts`; `routeTree.ts` assembles the tree. Kept apart from the tree so feature routes can
 * import `appRoute` without an import cycle.
 */
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import type { useAuth } from '@/auth/auth-provider';
import { LoginPage } from '@/auth/login-page';
import { NotFoundPage } from './pages/status-pages';
import { AppShell } from './shell/app-shell';

export interface RouterContext {
  queryClient: QueryClient;
  auth: ReturnType<typeof useAuth>;
}

export const BASEPATH = '/';

/** Only same-app paths: blocks `//evil.example` and absolute URLs (open-redirect guard). */
export const safeRedirect = (target: string | undefined) =>
  target && target.startsWith('/') && !target.startsWith('//') ? target : '/';

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

const loginSearch = z.object({ redirect: z.string().optional() });

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>) => loginSearch.parse(search),
  beforeLoad: ({ context, search }) => {
    if (context.auth.session) throw redirect({ href: safeRedirect(search.redirect) });
  },
  component: function LoginRoute() {
    const { redirect: target } = loginRoute.useSearch();
    const navigate = useNavigate();
    return <LoginPage onSignedIn={() => void navigate({ href: safeRedirect(target) })} />;
  },
});

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context, location }) => {
    if (!context.auth.session) throw redirect({ to: '/login', search: { redirect: location.href } });
  },
  component: AppShell,
});
